import React, { useEffect, useRef, useState } from "react";
import { Form, Col } from "react-bootstrap";
import uuid from "react-uuid";
import RadioGroup from "components/RadioGroup/RadioGroup";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import { TerceroManager } from "managers";
import { StyleHelper } from "utils";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import useMessage from "hooks/useMessage";
import useExceptionHandler from "hooks/useExceptionHandler";
import Select from "react-select";
import {
  Tercero,
  TIPO_DOCUMENTO_CEDULA,
  TIPO_DOCUMENTO_RNC,
  TIPO_DOCUMENTO_PASAPORTE,
} from "models/ECF/Tercero";
import { RncLookup, Simplebar } from "components";
import {
  RETENCIONES_ITBIS_CXC_OPTIONS,
  RETENCIONES_ISR_CXC_OPTIONS,
  RETENCIONES_ITBIS_CXP_OPTIONS,
  RETENCIONES_ISR_CXP_OPTIONS,
} from "models/ECF/Retenciones";

export default function TerceroEdit({
  showLoading,
  hideLoading,
  IdSec = undefined,
  showCanvas,
  setShowCanvas,
  updateList = () => { },
  onCreated = () => { },
}) {
  const [esNuevo, setEsNuevo] = useState(!IdSec);
  const [tercero, setTercero] = useState({});
  const [tituloHeader, setTituloHeader] = useState("Crear Tercero");
  const ref = useRef(null);
  const { Message, cerrarAlertas } = useMessage();
  const { handleException } = useExceptionHandler();
  const [rncLookupKey, setRncLookupKey] = useState(uuid());

  useEffect(() => {
    if (IdSec) {
      showLoading();
      cargarTercero(IdSec);
      setTituloHeader("Editar Tercero");
    } else {
      setDefaultValues();
      setTituloHeader("Crear Tercero");
    }
    hideLoading();
    cerrarAlertas();
    return () => {
      hideLoading();
      cerrarAlertas();
    };
  }, [IdSec]);

  const setDefaultValues = () => {
    setTercero({
      Documento: "",
      RazonSocial: "",
      Contacto: "",
      Correo: "",
      Direccion: "",
      CodigoInterno: "",
      TipoDocumento: "",
      TipoComprobanteVenta: "F",
      TipoComprobanteCompra: "S",
      EsCliente: false,
      EsProveedor: false,
      RetencionISRVenta: "",
      RetencionITBISVenta: "",
      NoAplicaITBISVenta: false,
      RetencionISRCompra: "",
      RetencionITBISCompra: "",
      NoAplicaITBISCompra: false,
      Estatus: Tercero.DEFAULT_ESTATUS_VALUE
    });

    setRncLookupKey(uuid());
  };

  const cargarTercero = (id) => {
    showLoading();
    TerceroManager.buscarPorId(id)
      .then((data) => {
        if (data && data.IdSec) {
          setTercero(data);
        } else {
          Message({ message: "Tercero no encontrado", type: "error" });
        }
      })
      .catch((e) => handleException(e))
      .finally(() => {
        hideLoading();
        updateList();
      });
  };

  function grabar() {
    showLoading();

    const terceroRequest = {
      ...(!esNuevo && { IdSec: tercero.IdSec }),
      RazonSocial: tercero.RazonSocial,
      Documento: tercero.Documento,
      TipoDocumento: tercero.TipoDocumento,
      CodigoInterno: tercero.CodigoInterno,
      Contacto: tercero.Contacto,
      Correo: tercero.Correo,
      Direccion: tercero.Direccion,
      EsCliente: tercero.EsCliente,
      EsProveedor: tercero.EsProveedor,
      Estatus: tercero.Estatus,

      ...(tercero.EsCliente && {
        TipoComprobanteVenta: tercero.TipoComprobanteVenta,
        RetencionITBISVenta: tercero.RetencionITBISVenta,
        RetencionISRVenta: tercero.RetencionISRVenta,
        NoAplicaITBISVenta: tercero.NoAplicaITBISVenta,
      }),

      ...(tercero.EsProveedor && {
        TipoComprobanteCompra: tercero.TipoComprobanteCompra,
        RetencionITBISCompra: tercero.RetencionITBISCompra,
        RetencionISRCompra: tercero.RetencionISRCompra,
        NoAplicaITBISCompra: tercero.NoAplicaITBISCompra,
      }),
    };

    if (esNuevo) {
      TerceroManager.crear(terceroRequest)
        .then((res) => {
          Message({ message: `Grabado Exitosamente` });
          onCreated(res);
          setDefaultValues();
          setShowCanvas(false);
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
          updateList();
        });
    } else {
      TerceroManager.actualizar(terceroRequest)
        .then((res) => {
          Message({ message: `Actualizado Exitosamente` });
          setDefaultValues();
          setShowCanvas(false);
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
          updateList();
        });
    }
  }

  const getTipoDocumentoPorLongitud = (documento) => {
    const len = (documento || "").length;
    if (len === 0) return "";
    if (len === 9) return TIPO_DOCUMENTO_RNC;
    if (len === 11) return TIPO_DOCUMENTO_CEDULA;
    return TIPO_DOCUMENTO_PASAPORTE;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTercero({
      ...tercero,
      [name]: value,
    });
  };

  const handleCleanClick = () => {
    setEsNuevo(true);
    setTituloHeader("Crear Tercero");
    setDefaultValues();
    cerrarAlertas();
  };

  return (
    <Simplebar
      ref={ref}
      backdrop={"static"}
      confirmBack
      placement={"end"}
      size="xxl"
      portal={false}
      visible={showCanvas}
      setShow={(e) => {
        setShowCanvas(e);
      }}
      useLoading={true}
      title={tituloHeader}
      btnLeft={{
        show: true,
        message: "Limpiar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "clean",
        isCenter: true,
        func: handleCleanClick,
      }}
      btnRight={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
    >
      <Col>
        <Form action="" className="form-horizontal" method="">
          <EditContainer>
            <EditSection title="Información General" style={{}}>
              <SectionRow>
                <InputContainer stacked label="Tipo de Tercero" labelClassName={"d-flex"}>
                  <div style={{ display: "flex", gap: "1.5rem" }}>
                    <Form.Check
                      id="EsCliente"
                      type="checkbox"
                      name="EsCliente"
                      checked={tercero.EsCliente || false}
                      onChange={(e) =>
                        setTercero({ ...tercero, EsCliente: e.target.checked })
                      }
                      label="Cliente"
                    />

                    <Form.Check
                      id="EsProveedor"
                      type="checkbox"
                      name="EsProveedor"
                      checked={tercero.EsProveedor || false}
                      onChange={(e) =>
                        setTercero({ ...tercero, EsProveedor: e.target.checked })
                      }
                      label="Proveedor"
                    />
                  </div>
                </InputContainer>

                <InputContainer stacked label="Nombre" labelClassName={"d-flex"}>
                  <Form.Control
                    type="text"
                    name="RazonSocial"
                    value={tercero.RazonSocial || ""}
                    onChange={handleChange}
                    placeholder="Razón Social"
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer stacked label="Documento" labelClassName={"d-flex"}>
                  <RncLookup
                    key={rncLookupKey}
                    initialValue={{
                      value: tercero.Documento,
                      label: tercero.Documento,
                      Rnc: tercero.Documento,
                      RazonSocial: tercero.RazonSocial,
                    }}
                    onChange={(e) => {
                      setTercero({
                        ...tercero,
                        Documento: e.Rnc,
                        RazonSocial: e.RazonSocial,
                        TipoDocumento: getTipoDocumentoPorLongitud(e.Rnc),
                      });
                    }}
                    required={true}
                  />
                </InputContainer>

                <InputContainer stacked label="Tipo Documento" labelClassName={"d-flex"}>
                  <RadioGroup
                    name="TipoDocumento"
                    options={Tercero.TIPO_DOCUMENTO_LIST}
                    selectedOption={tercero.TipoDocumento}
                    onChange={handleChange}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer stacked label="Estatus" labelClassName={"d-flex"}>
                  <RadioGroup
                    name="Estatus"
                    options={Tercero.ESTATUS_LIST}
                    selectedOption={tercero.Estatus}
                    onChange={handleChange}
                    disabled={esNuevo}
                  />
                </InputContainer>

                <InputContainer stacked label="Código" labelClassName={"d-flex"}>
                  <Form.Control
                    type="text"
                    name="CodigoInterno"
                    value={tercero.CodigoInterno || ""}
                    onChange={handleChange}
                    placeholder="Código Interno"
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            <EditSection title="Contacto" style={{}}>
              <SectionRow>
                <InputContainer stacked label="Telefono" labelClassName={"d-flex"}>
                  <Form.Control
                    type="text"
                    name="Contacto"
                    value={tercero.Contacto || ""}
                    onChange={handleChange}
                    placeholder="Telefono"
                  />
                </InputContainer>
                <InputContainer stacked label="Correo Electrónico" labelClassName={"d-flex"}>
                  <Form.Control
                    type="email"
                    name="Correo"
                    value={tercero.Correo || ""}
                    onChange={handleChange}
                    placeholder="Correo Electrónico"
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow multilinea>
                <InputContainer stacked label="Dirección" labelClassName={"d-flex"}>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="Direccion"
                    value={tercero.Direccion || ""}
                    onChange={handleChange}
                    placeholder="Calle, sector, ciudad/provincia, país"
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            {(tercero.EsCliente == true || tercero.EsProveedor == true) && (
              <>
                <EditSection title="Tipo de Comprobante" style={{}}>
                  {tercero.EsCliente && (
                    <SectionRow multilinea>
                      <InputContainer stacked label="Comprobante de Venta" labelClassName={"d-flex"}>
                        <RadioGroup
                          name="TipoComprobanteVenta"
                          options={Tercero.TIPO_COMPROBANTE_VENTA_LIST}
                          selectedOption={tercero.TipoComprobanteVenta}
                          onChange={handleChange}
                        />
                      </InputContainer>
                    </SectionRow>
                  )}

                  {tercero.EsProveedor && (
                    <SectionRow multilinea>
                      <InputContainer stacked label="Comprobante de Compra" labelClassName={"d-flex"}>
                        <RadioGroup
                          name="TipoComprobanteCompra"
                          options={Tercero.TIPO_COMPROBANTE_COMPRA_LIST}
                          selectedOption={tercero.TipoComprobanteCompra}
                          onChange={handleChange}
                        />
                      </InputContainer>
                    </SectionRow>
                  )}
                </EditSection>
              </>
            )}

            {tercero.EsCliente && (
              <EditSection title="Impuestos de Venta" style={{}}>
                <>
                  <SectionRow>
                    <InputContainer stacked label="Retención ITBIS Venta" labelClassName={"d-flex"}>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="RetencionITBISVenta"
                        options={RETENCIONES_ITBIS_CXC_OPTIONS}
                        value={tercero.RetencionITBISVenta
                          ? RETENCIONES_ITBIS_CXC_OPTIONS.find(o => o.value === tercero.RetencionITBISVenta)
                          : null}
                        onChange={(sel) => setTercero({ ...tercero, RetencionITBISVenta: sel ? sel.value : "" })}
                        placeholder="Seleccione Retención ITBIS Venta"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </InputContainer>

                    <InputContainer stacked label="Retención ISR Venta" labelClassName={"d-flex"}>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="RetencionISRVenta"
                        options={RETENCIONES_ISR_CXC_OPTIONS}
                        value={tercero.RetencionISRVenta
                          ? RETENCIONES_ISR_CXC_OPTIONS.find(o => o.value === tercero.RetencionISRVenta)
                          : null}
                        onChange={(sel) => setTercero({ ...tercero, RetencionISRVenta: sel ? sel.value : "" })}
                        placeholder="Seleccione Retención ISR Venta"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </InputContainer>
                  </SectionRow>

                  <SectionRow>
                    <InputContainer stacked label="No aplica" labelClassName={"d-flex"}>
                      <Form.Check
                        id="NoAplicaITBISVenta"
                        type="checkbox"
                        name="NoAplicaITBISVenta"
                        checked={tercero.NoAplicaITBISVenta || false}
                        onChange={(e) => setTercero({ ...tercero, NoAplicaITBISVenta: e.target.checked })}
                        label="ITBIS Venta"
                      />
                    </InputContainer>
                  </SectionRow>
                </>
              </EditSection>
            )}

            {tercero.EsProveedor && (
              <EditSection title="Impuestos de Compra" style={{}}>
                <>
                  <SectionRow>
                    <InputContainer stacked label="Retención ITBIS Compra" labelClassName={"d-flex"}>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="RetencionITBISCompra"
                        options={RETENCIONES_ITBIS_CXP_OPTIONS}
                        value={tercero.RetencionITBISCompra
                          ? RETENCIONES_ITBIS_CXP_OPTIONS.find(o => o.value === tercero.RetencionITBISCompra)
                          : null}
                        onChange={(sel) => setTercero({ ...tercero, RetencionITBISCompra: sel ? sel.value : "" })}
                        placeholder="Seleccione Retención ITBIS Compra"
                        isClearable
                        menuPortalTarget={document.body}
                        menuPlacement="top"
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </InputContainer>

                    <InputContainer stacked label="Retención ISR Compra" labelClassName={"d-flex"}>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="RetencionISRCompra"
                        options={RETENCIONES_ISR_CXP_OPTIONS}
                        value={tercero.RetencionISRCompra
                          ? RETENCIONES_ISR_CXP_OPTIONS.find(o => o.value === tercero.RetencionISRCompra)
                          : null}
                        onChange={(sel) => setTercero({ ...tercero, RetencionISRCompra: sel ? sel.value : "" })}
                        placeholder="Seleccione Retención ISR Compra"
                        isClearable
                        menuPortalTarget={document.body}
                        menuPlacement="top"
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </InputContainer>
                  </SectionRow>

                  <SectionRow>
                    <InputContainer stacked label="No aplica" labelClassName={"d-flex"}>
                      <Form.Check
                        id="NoAplicaITBISCompra"
                        type="checkbox"
                        name="NoAplicaITBISCompra"
                        checked={tercero.NoAplicaITBISCompra || false}
                        onChange={(e) => setTercero({ ...tercero, NoAplicaITBISCompra: e.target.checked })}
                        label="ITBIS Compra"
                      />
                    </InputContainer>
                  </SectionRow>
                </>
              </EditSection>
            )}
          </EditContainer>
        </Form>
      </Col>
    </Simplebar>
  );
}
